/**
 * ShakeDetector - Detects phone shaking motion using accelerometer
 * Emits events: shakeStart, shakeActive, shakeEnd
 * 
 * Usage:
 *   const detector = new ShakeDetector();
 *   detector.addEventListener('shakeStart', () => console.log('Shake started!'));
 *   detector.addEventListener('shakeEnd', (e) => console.log('Duration:', e.detail.duration));
 *   detector.start();
 */

export interface ShakeDetectorConfig {
  threshold?: number;       // m/s² acceleration threshold
  minShakes?: number;       // minimum movements to count
  timeWindow?: number;      // ms window for movements
  cooldown?: number;        // ms cooldown after shake ends
}

export interface ShakeEventDetail {
  duration?: number;
  timestamp?: number;
}

export class ShakeDetector extends EventTarget {
  private config: Required<ShakeDetectorConfig>;
  private isActive: boolean = false;
  private isShaking: boolean = false;
  private shakeStartTime: number | null = null;
  private lastShakeTime: number = 0;
  private shakeTimes: number[] = [];
  private checkTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(config: ShakeDetectorConfig = {}) {
    super();
    
    // Configuration with defaults
    this.config = {
      threshold: config.threshold ?? 15,
      minShakes: config.minShakes ?? 2,
      timeWindow: config.timeWindow ?? 600,
      cooldown: config.cooldown ?? 300,
    };
    
    // Bind methods
    this._handleMotion = this._handleMotion.bind(this);
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
  }
  
  /**
   * Starts listening for shake events
   */
  start(): void {
    if (this.isActive) return;
    
    this.isActive = true;
    window.addEventListener('devicemotion', this._handleMotion);
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }
  
  /**
   * Stops listening for shake events
   */
  stop(): void {
    if (!this.isActive) return;
    
    this.isActive = false;
    window.removeEventListener('devicemotion', this._handleMotion);
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    
    // Clear any pending timeout
    if (this.checkTimeoutId !== null) {
      clearTimeout(this.checkTimeoutId);
      this.checkTimeoutId = null;
    }
    
    // End any active shake
    if (this.isShaking) {
      this._endShake();
    }
  }
  
  /**
   * Pauses detection (battery optimization)
   */
  pause(): void {
    if (!this.isActive) return;
    window.removeEventListener('devicemotion', this._handleMotion);
    
    // End shake if pausing mid-shake
    if (this.isShaking) {
      this._endShake();
    }
  }
  
  /**
   * Resumes detection after pause
   */
  resume(): void {
    if (!this.isActive) return;
    window.addEventListener('devicemotion', this._handleMotion);
  }
  
  /**
   * Handles device motion events
   */
  private _handleMotion(event: DeviceMotionEvent): void {
    if (!event.accelerationIncludingGravity) return;
    
    const { x, y, z } = event.accelerationIncludingGravity;
    
    // Null checks for acceleration values
    if (x === null || y === null || z === null) return;
    
    // Calculate total acceleration magnitude
    const acceleration = Math.sqrt(x * x + y * y + z * z);
    
    // Detect if movement exceeds threshold
    if (acceleration > this.config.threshold) {
      this._recordShake();
    }
  }
  
  /**
   * Records a shake movement and checks if threshold met
   */
  private _recordShake(): void {
    const now = Date.now();
    
    // Add this shake time
    this.shakeTimes.push(now);
    
    // Remove old shake times outside the time window
    this.shakeTimes = this.shakeTimes.filter(
      time => now - time <= this.config.timeWindow
    );
    
    // Check if we have enough shakes within time window
    if (this.shakeTimes.length >= this.config.minShakes) {
      if (!this.isShaking) {
        this._startShake();
      } else {
        // Already shaking, emit active event
        this._updateShake();
      }
      this.lastShakeTime = now;
    }
  }
  
  /**
   * Starts a new shake sequence
   */
  private _startShake(): void {
    this.isShaking = true;
    this.shakeStartTime = Date.now();
    
    // Emit shakeStart event
    this.dispatchEvent(new CustomEvent('shakeStart', {
      detail: { timestamp: this.shakeStartTime }
    }));
    
    // Start checking for shake end
    this._checkShakeEnd();
  }
  
  /**
   * Updates an ongoing shake (continuous shaking)
   */
  private _updateShake(): void {
    if (this.shakeStartTime === null) return;
    
    const duration = Date.now() - this.shakeStartTime;
    
    // Emit shakeActive event for continuous shaking
    this.dispatchEvent(new CustomEvent('shakeActive', {
      detail: { duration }
    }));
  }
  
  /**
   * Checks if shake has ended (no movement for cooldown period)
   */
  private _checkShakeEnd(): void {
    if (!this.isShaking) return;
    
    const timeSinceLastShake = Date.now() - this.lastShakeTime;
    
    if (timeSinceLastShake >= this.config.cooldown) {
      this._endShake();
    } else {
      // Check again after remaining cooldown time
      const remainingCooldown = this.config.cooldown - timeSinceLastShake;
      this.checkTimeoutId = setTimeout(() => this._checkShakeEnd(), remainingCooldown + 10);
    }
  }
  
  /**
   * Ends the current shake sequence
   */
  private _endShake(): void {
    if (!this.isShaking || this.shakeStartTime === null) return;
    
    const duration = Date.now() - this.shakeStartTime;
    this.isShaking = false;
    this.shakeTimes = [];
    
    // Emit shakeEnd event with duration
    this.dispatchEvent(new CustomEvent('shakeEnd', {
      detail: { 
        duration,
        timestamp: Date.now()
      }
    }));
  }
  
  /**
   * Handles page visibility changes (battery optimization)
   */
  private _handleVisibilityChange(): void {
    if (document.hidden) {
      this.pause();
    } else {
      this.resume();
    }
  }
  
  /**
   * Gets current shake state
   */
  getState(): { isActive: boolean; isShaking: boolean; duration: number } {
    return {
      isActive: this.isActive,
      isShaking: this.isShaking,
      duration: this.isShaking && this.shakeStartTime !== null 
        ? Date.now() - this.shakeStartTime 
        : 0
    };
  }
}
